import { useEffect, useRef } from 'react';

const documents = [
  {
    icon: 'ri-file-chart-line',
    title: 'Full P&L Statements',
    detail: 'Last 3 years',
    color: 'bg-amber-50',
    iconColor: 'text-amber-700',
  },
  {
    icon: 'ri-government-line',
    title: 'Tax Returns',
    detail: 'Last 3 years',
    color: 'bg-forest-50',
    iconColor: 'text-forest',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Platform Analytics Screenshots',
    detail: 'Booking performance & traffic data',
    color: 'bg-slate-50',
    iconColor: 'text-slate-600',
  },
  {
    icon: 'ri-links-line',
    title: 'All Platform Listing URLs',
    detail: 'TripAdvisor, Booking.com & all OTAs',
    color: 'bg-amber-50',
    iconColor: 'text-amber-700',
  },
  {
    icon: 'ri-file-shield-2-line',
    title: 'Permit & License Documentation',
    detail: 'Full operational compliance',
    color: 'bg-forest-50',
    iconColor: 'text-forest',
  },
  {
    icon: 'ri-search-eye-line',
    title: 'Property Inspection Reports',
    detail: 'Structural and systems assessments',
    color: 'bg-slate-50',
    iconColor: 'text-slate-600',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipment & Appliance List',
    detail: 'With ages and condition details',
    color: 'bg-amber-50',
    iconColor: 'text-amber-700',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Current Booking Calendar',
    detail: 'Forward reservations and occupancy',
    color: 'bg-forest-50',
    iconColor: 'text-forest',
  },
];

export default function AvailableOnRequest() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      }),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="documents" ref={sectionRef} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

          {/* Left — Title */}
          <div className="w-full lg:w-[38%] lg:sticky lg:top-28 reveal">
            <div className="inline-flex items-center gap-2 border border-gold/50 text-gold rounded-full px-4 py-1.5 mb-5">
              <i className="ri-lock-2-line text-sm w-4 h-4 flex items-center justify-center" />
              <span className="font-sans text-xs tracking-widest uppercase">Due Diligence Package</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-5">
              Available<br /><em>Upon Request</em>
            </h2>
            <p className="font-sans text-sm text-gray-600 leading-relaxed mb-6">
              A complete due diligence package is available to qualified, serious buyers upon execution of a Non-Disclosure Agreement. All documentation reflects the most recent operational and financial data.
            </p>
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gold rounded-lg shrink-0 mt-0.5">
                  <i className="ri-shield-keyhole-line text-white text-sm" />
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-forest-dark">Strictly Confidential</p>
                  <p className="font-sans text-xs text-gray-600 mt-1 leading-relaxed">
                    This brochure is intended for prospective buyers only. All information is provided in confidence and must not be shared or reproduced without prior written consent.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-forest text-white font-sans text-sm tracking-widest uppercase rounded-md hover:bg-forest-dark transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
              Request Documents
            </button>
          </div>

          {/* Right — Document cards */}
          <div className="w-full lg:w-[62%] grid grid-cols-1 sm:grid-cols-2 gap-4">
            {documents.map((doc, idx) => (
              <div
                key={doc.title}
                className="reveal flex items-start gap-4 bg-cream rounded-xl p-5 border border-cream-dark hover:border-gold/40 transition-all duration-300"
                style={{ transitionDelay: `${idx * 0.07}s` }}
              >
                <div className={`w-10 h-10 flex items-center justify-center ${doc.color} rounded-lg shrink-0`}>
                  <i className={`${doc.icon} ${doc.iconColor} text-lg`} />
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-forest-dark">{doc.title}</p>
                  <p className="font-sans text-xs text-gray-500 mt-1">{doc.detail}</p>
                </div>
                <div className="ml-auto shrink-0">
                  <span className="font-sans text-xs bg-gold/10 text-gold rounded-full px-2.5 py-1 whitespace-nowrap">On Request</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
